import { Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../../components/Spin";
import { Label } from "../../../components/ui/label";
import CustomLabelText from "../../../components/CustomLabelText";
import { Input } from "../../../components/ui/input";
import { API, fileUpload, get } from "../../../config";
import { Button } from "../../../components/ui/button";
import { AddAdminSuburbSchema } from "../../../lib/Validation";
import { useStateList } from "../../../lib/OptionsAPI";
import SearchSelect from "../../../components/SearchSelect";
import { Loader2 } from "lucide-react";

const AddEditSuburb = ({
  id,
  toggleShowAddEditSuburb,
  edit,
  fetchSuburbData,
}) => {
  const [suburbData, setSuburbData] = useState([]);
  const [loading, setLoading] = useState(false);

  // State
  const { stateLoading, stateList = [] } = useStateList();
  const stateListing = stateList?.map((item) => {
    const { id: value, name, short_name, ...rest } = item;
    return { label: name + ` (${short_name})`, value, ...rest };
  });
  const state = stateListing?.find(
    (item) => item?.value === (suburbData?.state_id ? suburbData?.state_id : "")
  )?.value;

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.GET_ADMIN_SUBURB_LIST}/` + id);
      setSuburbData(reponse?.data?.data);
      setLoading(false);
    } catch (e) {
      setSuburbData(null);
    }
  }

  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFormSubmit = async (value) => {
    const formData = new FormData();

    formData.append("name", value?.name ? value?.name : "");
    formData.append("state_id", value?.state_id ? value?.state_id : "");

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.GET_ADMIN_SUBURB_LIST}${edit ? `/${id}` : ""}`,
        formData
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditSuburb();
        fetchSuburbData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <Formik
      initialValues={{
        name: suburbData?.name,
        state_id: state,
      }}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddAdminSuburbSchema}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isValid,
      }) => (
        <>
          {loading && <Spin fullscreen={true} />}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-section md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden max-h-[80vh] p-4 border rounded-lg grid gap-4">
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="state_id">
                    <CustomLabelText
                      required={false}
                      labelText="State"
                      errorField={errors?.state_id}
                    />
                  </Label>
                  <div className="relative">
                    <SearchSelect
                      frameworks={stateListing}
                      optionType={"State"}
                      clearable={true}
                      onClear={() => {
                        setFieldValue("state_id", "");
                      }}
                      handleSelect={(e) => {
                        setFieldValue("state_id", e);
                      }}
                      handleBlur={handleBlur}
                      className={
                        errors?.state_id &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                      disabled={false}
                      initialValue={values?.state_id}
                    />
                    {stateLoading && (
                      <span className="absolute top-2 right-2 animate-spin">
                        <Loader2 width={20} height={20} />
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="name">
                    <CustomLabelText
                      required={true}
                      labelText="Name"
                      errorField={errors?.name}
                    />
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    name="name"
                    value={values?.name ? values?.name : ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    autoComplete="off"
                    onKeyPress={(e) => {
                      e.which === 13 && e.preventDefault();
                    }}
                    className={
                      errors?.name &&
                      `focus-visible:ring-red-500 border-red-500`
                    }
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditSuburb();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditSuburb;
